/**
 * Calls and notes saved on a person and not yet in the record (the Undo window, then the write and the
 * refetch): the timeline shows each at once, and drops it on Undo, on a failed write, or once the record has it.
 */
import { useSyncExternalStore } from "react";

export interface Pending {
  key: string;
  personId: number;
  kind: "call" | "note";
  body: string;
  /** ISO: when it was saved. */
  at: string;
}

let items: readonly Pending[] = [];
const listeners = new Set<() => void>();
const emit = () => listeners.forEach((listener) => listener());
let next = 0;

/** Shows a save on the timeline now; the returned function takes it off. */
export function addPending(personId: number, kind: Pending["kind"], body: string): () => void {
  const key = `pending:${(next += 1)}`;
  items = [...items, { key, personId, kind, body, at: new Date().toISOString() }];
  emit();
  return () => {
    items = items.filter((item) => item.key !== key);
    emit();
  };
}

const subscribe = (listener: () => void) => {
  listeners.add(listener);
  return () => void listeners.delete(listener);
};

/** The person's saves not yet in the record, oldest first. */
export function usePending(personId: number): readonly Pending[] {
  const all = useSyncExternalStore(subscribe, () => items);
  return all.filter((item) => item.personId === personId);
}
